import React, { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import z from 'zod';
import ChatImage from '../ChatImage';
import { WebSearchResultSchema } from './tools/web-search';
import { ChevronLeft, ChevronRight, X, ExternalLink, Download, ArrowLeft, ArrowRight } from 'lucide-react';

/**
 * ChatBubbleImageSearchResults component displays search image results in a horizontal scrollable container
 * @param {Object} props
 * @param {z.infer<typeof WebSearchResultSchema>["images"]} props.imageResults - Array of image search results from getAllMessageSearchImageResults
 */
function ChatBubbleImageSearchResults({ imageResults }) {
  const scrollContainerRef = useRef(null);
  const [showLeftButton, setShowLeftButton] = useState(false);
  const [showRightButton, setShowRightButton] = useState(true);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [isViewerOpen, setIsViewerOpen] = useState(false);

  useEffect(() => {
    if (!imageResults || imageResults.length === 0) return;

    const container = scrollContainerRef.current;
    if (!container) return;

    // Initial check
    updateButtonVisibility();

    // Add scroll listener
    container.addEventListener('scroll', updateButtonVisibility);

    // Add resize listener to handle container size changes
    const resizeObserver = new ResizeObserver(updateButtonVisibility);
    resizeObserver.observe(container);

    return () => {
      container.removeEventListener('scroll', updateButtonVisibility);
      resizeObserver.disconnect();
    };
  }, [imageResults]);

  // Keyboard navigation for image viewer
  useEffect(() => {
    if (!isViewerOpen) return;

    const handleKeyDown = (e) => {
      switch (e.key) {
        case 'Escape':
          closeImageViewer();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          navigateImage('prev');
          break;
        case 'ArrowRight':
          e.preventDefault();
          navigateImage('next');
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isViewerOpen, selectedImageIndex, imageResults.length]);

  if (!imageResults || imageResults.length === 0) return null;

  const openImageViewer = (index) => {
    setSelectedImageIndex(index);
    setIsViewerOpen(true);
  };

  const closeImageViewer = () => {
    setIsViewerOpen(false);
    setSelectedImageIndex(null);
  };

  const navigateImage = (direction) => {
    if (selectedImageIndex === null) return;
    const newIndex = direction === 'next'
      ? (selectedImageIndex + 1) % imageResults.length
      : (selectedImageIndex - 1 + imageResults.length) % imageResults.length;
    setSelectedImageIndex(newIndex);
  };

  const selectedImage = selectedImageIndex !== null ? imageResults[selectedImageIndex] : null;

  const updateButtonVisibility = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    const threshold = 10; // pixels

    setShowLeftButton(scrollLeft > threshold);
    setShowRightButton(scrollLeft < scrollWidth - clientWidth - threshold);
  };

  const scrollLeft = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const scrollAmount = container.clientWidth * 0.75; // 75% of viewport width
    container.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
  };

  const scrollRight = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const scrollAmount = container.clientWidth * 0.75; // 75% of viewport width
    container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
  };

  return (
    <div className="relative max-w-full min-w-0 mb-4 isolate">
      {/* Left scroll button */}
      {showLeftButton && (
        <button
          onClick={scrollLeft}
          className="absolute left-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-popover/90 hover:bg-muted border border-border rounded-full shadow-md flex items-center justify-center transition-all duration-200 hover:scale-110"
          aria-label="Scroll left"
        >
          <ChevronLeft className="w-4 h-4 text-foreground" />
        </button>
      )}

      {/* Right scroll button */}
      {showRightButton && (
        <button
          onClick={scrollRight}
          className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-popover/90 hover:bg-muted border border-border rounded-full shadow-md flex items-center justify-center transition-all duration-200 hover:scale-110"
          aria-label="Scroll right"
        >
          <ChevronRight className="w-4 h-4 text-foreground" />
        </button>
      )}

      <div
        ref={scrollContainerRef}
        className="flex overflow-x-auto gap-3 py-2 no-scrollbar scroll-smooth snap-x snap-mandatory"
      >
        {imageResults.map((image, index) => (
          <div
            key={image.url || index}
            className="flex-shrink-0 snap-start"
          >
            <button
              onClick={() => openImageViewer(index)}
              className="w-24 h-24 sm:w-40 sm:h-40 rounded-lg overflow-hidden border border-border transition-all duration-200 hover:border-primary hover:scale-105 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background"
            >
              <img
                src={image.thumbnail}
                alt={image.title}
                className="object-cover w-full h-full"
              />
            </button>
          </div>
        ))}
      </div>

      {/* Image Viewer Modal - Rendered via Portal */}
      {isViewerOpen && selectedImage && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
          <div className="relative max-w-7xl max-h-[90vh] w-full mx-4">
            {/* Header */}
            <div className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-background/90 to-transparent p-4">
              <div className="flex items-center justify-between text-foreground min-w-0">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <span className="text-sm bg-muted shrink-0 px-2 py-1 rounded-full text-muted-foreground">
                    {selectedImageIndex + 1} of {imageResults.length}
                  </span>
                  <h3 className="text-lg font-medium truncate min-w-0">{selectedImage.title}</h3>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <a
                    href={selectedImage.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 hover:bg-muted rounded-full transition-colors"
                    title="View source"
                  >
                    <ExternalLink className="w-5 h-5" />
                  </a>
                  <a
                    href={selectedImage.original}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 hover:bg-muted rounded-full transition-colors"
                    title="Download image"
                  >
                    <Download className="w-5 h-5" />
                  </a>
                  <button
                    onClick={closeImageViewer}
                    className="p-2 hover:bg-muted rounded-full transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>

            {/* Navigation Buttons */}
            {imageResults.length > 1 && (
              <>
                <button
                  onClick={() => navigateImage('prev')}
                  className="absolute left-4 top-1/2 -translate-y-1/2 z-10 p-3 bg-popover/90 hover:bg-muted border border-border text-foreground rounded-full transition-colors shadow-lg"
                >
                  <ArrowLeft className="w-6 h-6" />
                </button>
                <button
                  onClick={() => navigateImage('next')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 z-10 p-3 bg-popover/90 hover:bg-muted border border-border text-foreground rounded-full transition-colors shadow-lg"
                >
                  <ArrowRight className="w-6 h-6" />
                </button>
              </>
            )}

            {/* Image */}
            <div className="bg-popover rounded-lg overflow-hidden shadow-2xl border border-border">
              <div className="bg-muted/30 p-2">
                <img
                  src={selectedImage.original}
                  alt={selectedImage.title}
                  className="w-full h-auto max-h-[70vh] object-contain rounded"
                  onError={(e) => {
                    e.target.src = selectedImage.thumbnail;
                  }}
                />
              </div>

              {/* Footer with metadata */}
              <div className="p-4 bg-popover border-t border-border">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <a
                      href={selectedImage.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-medium text-card-foreground hover:text-primary transition-colors truncate block"
                    >
                      {selectedImage.title}
                    </a>
                    <p className="text-xs text-muted-foreground mt-1">Source: {selectedImage.source}</p>
                  </div>
                  <div className="ml-4 flex-shrink-0">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20">
                      #{selectedImage.position}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Click outside to close */}
          <div
            className="absolute inset-0 -z-10"
            onClick={closeImageViewer}
          />
        </div>,
        document.body
      )}
    </div>
  );
}

export default ChatBubbleImageSearchResults;